import React from 'react';
import {
  Box,
  Flex,
  HStack,
  Text,
  Button,
  IconButton,
  Container,
  Image,
  Tag,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { FiArrowUpRight } from 'react-icons/fi';
import { useLegalLang } from '../LegalLangContext';
import { APP_META } from '../content/appMeta';
import waeboxIcon from 'assets/img/waebox-icon.png';

const LangButton = ({ value, active, onClick, label }) => (
  <Button
    size="xs"
    variant={active ? 'solid' : 'ghost'}
    colorScheme={active ? 'brand' : undefined}
    bg={active ? 'brand.600' : 'transparent'}
    color={active ? 'white' : 'text.secondary'}
    _hover={{ bg: active ? 'brand.700' : 'gray.100' }}
    fontWeight="600"
    px={3}
    h="28px"
    onClick={() => onClick(value)}
    aria-pressed={active}
  >
    {label}
  </Button>
);

const LegalHeader = () => {
  const { lang, setLang, t } = useLegalLang();
  const location = useLocation();
  const isIndex = location.pathname === '/legal' || location.pathname === '/legal/';
  const showCompactBack = useBreakpointValue({ base: true, md: false });

  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={20}
      bg="rgba(255,255,255,0.85)"
      backdropFilter="saturate(180%) blur(12px)"
      borderBottom="1px solid"
      borderColor="gray.200"
    >
      <Container maxW="6xl" px={{ base: 4, md: 6 }}>
        <Flex h="64px" align="center" justify="space-between" gap={3}>
          {/* Brand */}
          <RouterLink to="/legal" aria-label={`${APP_META.name} — ${t('brandTagline')}`}>
            <HStack spacing={3}>
              <Image
                src={waeboxIcon}
                alt={`${APP_META.name} logo`}
                boxSize="36px"
                borderRadius="10px"
              />
              <Box display={{ base: 'none', sm: 'block' }}>
                <Text fontWeight="700" fontSize="md" color="text.primary" lineHeight="1.1">
                  {APP_META.name}
                </Text>
                <Text fontSize="xs" color="text.muted" lineHeight="1.2" mt="2px">
                  {t('brandTagline')}
                </Text>
              </Box>
            </HStack>
          </RouterLink>

          {/* Actions */}
          <HStack spacing={2}>
            {!isIndex && (
              <Button
                as={RouterLink}
                to="/legal"
                size="sm"
                variant="ghost"
                color="text.secondary"
                _hover={{ bg: 'gray.100', color: 'text.primary' }}
                fontWeight="500"
              >
                {showCompactBack ? t('back') : t('backToLegal')}
              </Button>
            )}

            <HStack
              spacing={0}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="md"
              p="2px"
              bg="gray.50"
            >
              <LangButton value="en" active={lang === 'en'} onClick={setLang} label="EN" />
              <LangButton value="vi" active={lang === 'vi'} onClick={setLang} label="VI" />
            </HStack>

            <Button
              as="a"
              href="/admin/dashboard"
              size="sm"
              variant="outline"
              borderColor="gray.300"
              color="text.primary"
              _hover={{ bg: 'gray.50', borderColor: 'brand.500', color: 'brand.700' }}
              rightIcon={<FiArrowUpRight />}
              fontWeight="500"
              display={{ base: 'none', md: 'inline-flex' }}
            >
              {t('backToDashboard')}
            </Button>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
};

export default LegalHeader;
